import React, { useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useService } from '../store/slice/serviceSlice';
import { useSalon } from '../store/slice/salonSlice';
import { useCategory } from '../store/slice/categorySlice';
import AdvancedFilter from '../components/AdvancedFilter';
import { ActionButton, ActionButtonGroup, StatusToggle } from '../components/ActionControls';

function ServicesPage() {
  const {
    services,
    loading,
    error,
    fetchServices,
    createService,
    updateService,
    toggleServiceActive,
    deleteService,
  } = useService();
  const { salons, fetchSalons } = useSalon();
  const { categories, fetchCategories } = useCategory();

  const [formData, setFormData] = useState({
    salon_id: '',
    category_id: '',
    name: '',
    description: '',
    base_price: '',
    duration_minutes: '',
    is_active: true,
  });
  const [showForm, setShowForm] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    priceMin: '',
    priceMax: '',
    durationMin: '',
    durationMax: '',
    sortBy: '',
  });

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    fetchServices();
    fetchSalons();
    fetchCategories();
  }, [fetchCategories, fetchSalons, fetchServices]);

  const handleFilterChange = (key, value) => {
    if (key === 'priceRange') {
      setFilters((p) => ({ ...p, priceMin: value.min || '', priceMax: value.max || '' }));
    } else if (key === 'durationRange') {
      setFilters((p) => ({ ...p, durationMin: value.min || '', durationMax: value.max || '' }));
    } else {
      setFilters((p) => ({ ...p, [key]: value }));
    }
  };

  const filterConfig = [
    { key: 'search', type: 'search', value: filters.search },
    {
      key: 'priceRange',
      type: 'numberRange',
      label: 'Price ($)',
      value: { min: filters.priceMin, max: filters.priceMax },
      minPlaceholder: 'Min',
      maxPlaceholder: 'Max',
    },
    {
      key: 'durationRange',
      type: 'numberRange',
      label: 'Duration (mins)',
      value: { min: filters.durationMin, max: filters.durationMax },
      minPlaceholder: 'Min',
      maxPlaceholder: 'Max',
    },
    {
      key: 'sortBy',
      type: 'sort',
      label: 'Sort By',
      value: filters.sortBy,
      defaultValue: '',
      options: [
        { value: 'name-asc', label: 'Name (A-Z)' },
        { value: 'name-desc', label: 'Name (Z-A)' },
        { value: 'price-asc', label: 'Lowest price' },
        { value: 'price-desc', label: 'Highest price' },
        { value: 'duration-asc', label: 'Shortest first' },
        { value: 'duration-desc', label: 'Longest first' },
      ],
    },
  ];

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.priceMin || filters.priceMax) count++;
    if (filters.durationMin || filters.durationMax) count++;
    if (filters.sortBy) count++;
    return count;
  }, [filters]);

  const filteredServices = useMemo(() => {
    let result = [...services];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter((s) => (s.name || '').toLowerCase().includes(term));
    }
    const getPrice = (s) => parseFloat(s.base_price ?? s.price ?? 0);
    const getDuration = (s) => parseInt(s.duration_minutes ?? s.duration ?? 0, 10);
    if (filters.priceMin) {
      const min = parseFloat(filters.priceMin);
      if (!isNaN(min)) result = result.filter((s) => getPrice(s) >= min);
    }
    if (filters.priceMax) {
      const max = parseFloat(filters.priceMax);
      if (!isNaN(max)) result = result.filter((s) => getPrice(s) <= max);
    }
    if (filters.durationMin) {
      const min = parseInt(filters.durationMin, 10);
      if (!isNaN(min)) result = result.filter((s) => getDuration(s) >= min);
    }
    if (filters.durationMax) {
      const max = parseInt(filters.durationMax, 10);
      if (!isNaN(max)) result = result.filter((s) => getDuration(s) <= max);
    }
    if (filters.sortBy) {
      const [field, order] = filters.sortBy.split('-');
      result.sort((a, b) => {
        let va = field === 'price' ? getPrice(a) : field === 'duration' ? getDuration(a) : (a.name || '').toLowerCase();
        let vb = field === 'price' ? getPrice(b) : field === 'duration' ? getDuration(b) : (b.name || '').toLowerCase();
        if (order === 'desc') [va, vb] = [vb, va];
        return va < vb ? -1 : va > vb ? 1 : 0;
      });
    }

    return result;
  }, [services, filters]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      salon_id: '',
      category_id: '',
      name: '',
      description: '',
      base_price: '',
      duration_minutes: '',
      is_active: true,
    });
    setEditingServiceId(null);
  };

  const handleSubmitService = async (e) => {
    e.preventDefault();
    const { salon_id, category_id, name, description, base_price, duration_minutes, is_active } = formData;
    if (!salon_id || !category_id || !name || !base_price || !duration_minutes) return;

    const basePrice = parseFloat(base_price);
    const durationMinutes = parseInt(duration_minutes, 10);
    if (isNaN(basePrice) || basePrice < 0 || isNaN(durationMinutes) || durationMinutes <= 0) return;

    const payload = {
      salon_id: parseInt(salon_id, 10),
      category_id: parseInt(category_id, 10),
      name: name.trim(),
      description: description?.trim() || null,
      base_price: basePrice,
      duration_minutes: durationMinutes,
      is_active: is_active ? 1 : 0,
    };

    const result = editingServiceId
      ? await updateService({ id: editingServiceId, serviceData: payload })
      : await createService(payload);

    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(editingServiceId ? 'Service updated successfully' : 'Service added successfully');
      resetForm();
      setShowForm(false);
    } else if (result?.meta?.requestStatus === 'rejected') {
      toast.error(result?.payload || (editingServiceId ? 'Failed to update service' : 'Failed to add service'));
    }
  };

  const handleEditService = (service) => {
    setEditingServiceId(service.id);
    setFormData({
      salon_id: String(service.salon_id ?? ''),
      category_id: String(service.category_id ?? ''),
      name: service.name ?? '',
      description: service.description ?? '',
      base_price: String(service.base_price ?? service.price ?? ''),
      duration_minutes: String(service.duration_minutes ?? service.duration ?? ''),
      is_active: Boolean(service.is_active),
    });
    setShowForm(true);
  };

  const handleToggleServiceStatus = async (id, isActive) => {
    const result = await toggleServiceActive(id);
    if (result?.meta?.requestStatus === 'fulfilled') {
      toast.success(`Service marked as ${isActive ? 'inactive' : 'active'}`);
    } else {
      toast.error(result?.payload || 'Failed to update service status');
    }
  };

  const handleDeleteService = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      const result = await deleteService(id);
      if (result?.meta?.requestStatus === 'fulfilled') {
        toast.success('Service deleted successfully');
      }
    }
  };

  if (loading && services.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-500">Loading services...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl text-slate-800 tracking-tight">Services</h1>
          <p className="mt-1 text-slate-500">Manage your salon services and pricing</p>
        </div>
        <button
          onClick={() => {
            if (showForm) resetForm();
            setShowForm(!showForm);
          }}
          className="btn-primary inline-flex items-center gap-2"
        >
          <span>{showForm ? '−' : '+'}</span> {showForm ? 'Cancel' : editingServiceId ? 'Edit Service' : 'Add Service'}
        </button>
      </div>

    

      {showForm && (
        <form onSubmit={handleSubmitService} className="card-premium p-6 space-y-4 max-w-xl">
          <h3 className="font-semibold text-slate-800">{editingServiceId ? 'Edit Service' : 'Add New Service'}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Salon <span className="text-red-500">*</span></label>
              <select
                name="salon_id"
                value={formData.salon_id}
                onChange={handleFormChange}
                className="input-premium"
                required
                disabled={loading}
              >
                <option value="">Select salon</option>
                {salons.map((s) => (
                  <option key={s.id} value={s.id}>{s.name || `Salon #${s.id}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Category <span className="text-red-500">*</span></label>
              <select
                name="category_id"
                value={formData.category_id}
                onChange={handleFormChange}
                className="input-premium"
                required
                disabled={loading}
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name || `Category #${c.id}`}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Service Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="name"
              placeholder="e.g., Hair Cut"
              value={formData.name}
              onChange={handleFormChange}
              className="input-premium"
              required
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
            <textarea
              name="description"
              placeholder="Brief description of this service"
              value={formData.description}
              onChange={handleFormChange}
              rows="3"
              className="input-premium resize-none"
              disabled={loading}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Base Price ($) <span className="text-red-500">*</span></label>
              <input
                type="number"
                name="base_price"
                placeholder="0.00"
                step="0.01"
                min="0"
                value={formData.base_price}
                onChange={handleFormChange}
                className="input-premium"
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Duration (mins) <span className="text-red-500">*</span></label>
              <input
                type="number"
                name="duration_minutes"
                placeholder="30"
                min="1"
                value={formData.duration_minutes}
                onChange={handleFormChange}
                className="input-premium"
                required
                disabled={loading}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleFormChange}
              disabled={loading}
              className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Active</label>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (editingServiceId ? 'Updating...' : 'Adding...') : (editingServiceId ? 'Update Service' : 'Add Service')}
          </button>
        </form>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Services</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">{services.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500"># Filtered</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">{filteredServices.length}</p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Avg. Price</p>
          <p className="mt-1 text-2xl font-bold text-slate-800">
            ${services.length ? (services.reduce((s, x) => s + parseFloat(x.base_price ?? x.price ?? 0), 0) / services.length).toFixed(2) : '0'}
          </p>
        </div>
        <div className="card-premium p-5">
          <p className="text-sm font-medium text-slate-500">Total Revenue</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">
            ${services.reduce((s, x) => s + parseFloat(x.base_price ?? x.price ?? 0), 0).toFixed(2)}
          </p>
        </div>
      </div>

      <AdvancedFilter
        searchPlaceholder="Search services by name..."
        filters={filterConfig}
        onFilterChange={handleFilterChange}
        activeCount={activeFilterCount}
      />

      <div className="card-premium overflow-hidden">
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/80">
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Service</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Salon</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Category</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Price</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Duration</th>
                <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map((service) => (
                <tr key={service.id} className="border-b border-slate-50 hover:bg-slate-50/60 transition-colors">
                  <td className="py-4 px-6 font-medium text-slate-800">{service.name}</td>
                  <td className="py-4 px-6 text-slate-600">{service.salon_id != null ? (salons.find((s) => s.id === service.salon_id)?.name ?? `#${service.salon_id}`) : '–'}</td>
                  <td className="py-4 px-6 text-slate-600">{service.category_id != null ? (categories.find((c) => c.id === service.category_id)?.name ?? `#${service.category_id}`) : '–'}</td>
                  <td className="py-4 px-6 text-slate-600">${parseFloat(service.base_price ?? service.price ?? 0).toFixed(2)}</td>
                  <td className="py-4 px-6 text-slate-600">{service.duration_minutes ?? service.duration ?? 0} mins</td>
                  <td className="py-4 px-6">
                    <span className={`status-pill ${service.is_active ? 'status-pill-success' : 'status-pill-muted'}`}>
                      {service.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <ActionButtonGroup>
                      <ActionButton
                        type="button"
                        variant="edit"
                        onClick={() => handleEditService(service)}
                        disabled={loading}
                      >
                        Edit
                      </ActionButton>
                      <StatusToggle
                        checked={Boolean(service.is_active)}
                        onChange={() => handleToggleServiceStatus(service.id, service.is_active)}
                        disabled={loading}
                        label={`Toggle service status for ${service.name}`}
                        showStateLabel={false}
                      />
                      <ActionButton
                        type="button"
                        variant="danger"
                        onClick={() => handleDeleteService(service.id)}
                        disabled={loading}
                      >
                        Delete
                      </ActionButton>
                    </ActionButtonGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredServices.length === 0 && (
          <div className="py-16 text-center text-slate-500">No services found. Add one to get started!</div>
        )}
      </div>
    </div>
  );
}

export default ServicesPage;
